/* kunstli-inkybay-gallery-sync.js
 * Keeps the theme's slider gallery and InkyBay's option preview
 * from fighting over the same <img class="product-image"> elements.
 *
 * Works for every InkyBay option type that has an image attached:
 * swatch, radio and dropdown (plus InkyBay's default colour swatches).
 * Text/number/upload fields and options without an image are ignored.
 *
 * Rules:
 *  - Before any option pick: every slide shows its real photo.
 *  - Option pick: gallery scrolls to the HERO slide (first image) and
 *    shows the option preview there, wherever InkyBay wrote it.
 *    The hero's hover-zoom image is swapped too, so zoom shows the preview.
 *  - The preview is temporary: as soon as the shopper browses the gallery
 *    (thumb, arrow, swipe/scroll) the hero gets its real photo back.
 *  - Clicking the same swatch/radio again re-shows its preview (InkyBay does
 *    not fire again for an already-selected option, so we remember it).
 *  - All other slides always keep their real photos.
 *
 * Speed: InkyBay rebuilds every option image as a huge base64 PNG on a
 * canvas on each click (slow). For options that carry an image file
 * (pro_image_part) we skip that and show InkyBay's original file straight
 * from its CDN, preloaded quietly after page load, so clicks are instant.
 * InkyBay's own (slow) data:image/png write is ignored for those options.
 */
(function () {
  'use strict';

  var GALLERY_SELECTOR = 'media-gallery';
  var SLIDE_SELECTOR = '.media-viewer__item';
  var IMAGE_SELECTOR = '.product-image';
  var ZOOM_IMAGE_SELECTOR = '.js-zoom-image';
  var ZOOM_LINK_SELECTOR = '.js-zoom-link';
  // Clickable option containers (click = pick, also when already selected)
  var SWATCH_SELECTOR = '.inkybay_default_color_swatch_cover, .inkybay_option_swatch, .inkybay_option_radio';
  // Every InkyBay option set on the product page (change events come from here)
  var OPTION_SET_SELECTOR = '[id^="INKYBAY_OPTION_SET_"]';
  var SWATCH_SELECTED_CLASS = 'idcs_sel';
  // InkyBay CDN folder for option images; store id is read from the page,
  // this is only the fallback
  var OPTION_IMAGE_BASE_FALLBACK = 'https://cdn.inkybay.com/shopify/resources/1749859461532/option_images/';

  var imageObserver = null;
  var deselectObserver = null;
  var hasClickedSwatch = false;
  var previewSrc = null;          // image currently shown as preview on the hero
  var previewActive = false;      // hero shows the preview until the shopper browses
  var previewBySwatch = new WeakMap(); // option element (input/<option>/swatch) -> its preview image
  var lastSwatchEl = null;        // option the shopper picked last
  var lastPickIsDirect = false;   // last pick is shown from the CDN file, ignore InkyBay's write
  var optionImageBase = null;     // resolved CDN folder for option images
  var acceptWritesUntil = 0;      // InkyBay writes only count shortly after an option pick
  var ACCEPT_WINDOW_MS = 5000;
  var autoScrolling = false;      // true while WE scroll to the hero (not the shopper)
  var originalSrcByIndex = null;  // filled once /products/<handle>.json resolves

  function getGallery() {
    return document.querySelector(GALLERY_SELECTOR);
  }

  function getCurrentSlide(gallery) {
    return (gallery && gallery.currentItem)
      || gallery.querySelector(SLIDE_SELECTOR + '.is-current-variant')
      || gallery.querySelector(SLIDE_SELECTOR);
  }

  function getSlides(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll(SLIDE_SELECTOR));
  }

  // Video/model slides also render a .product-image poster, but Shopify's
  // product JSON only lists actual images - so image-only slides are
  // matched by position against it.
  function getImageSlides(gallery) {
    return Array.prototype.slice.call(
      gallery.querySelectorAll(SLIDE_SELECTOR + '[data-media-type="image"]')
    );
  }

  function getHeroSlide(gallery) {
    return getImageSlides(gallery)[0] || null;
  }

  function getOriginalSrc(slideEl) {
    if (!originalSrcByIndex || !slideEl) return null;
    var gallery = slideEl.closest(GALLERY_SELECTOR);
    return originalSrcByIndex[getImageSlides(gallery).indexOf(slideEl)] || null;
  }

  function loadOriginalImages(gallery) {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return Promise.resolve();

    return fetch('/products/' + match[1] + '.json')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var images = (data.product && data.product.images) || [];
        var imageSlides = getImageSlides(gallery);
        // Positions line up with product.media render order; if they don't
        // match 1:1 bail rather than guess wrong.
        if (images.length !== imageSlides.length) return;
        originalSrcByIndex = images.map(function (image) { return image.src; });
      })
      .catch(function () {});
  }

  function withoutObservingImages(gallery, fn) {
    if (imageObserver) imageObserver.disconnect();
    fn();
    if (imageObserver && gallery) startObservingImages(gallery);
  }

  /* ---------- Hover-zoom image (theme renders it separately) ---------- */

  function setZoomImage(slideEl, src) {
    var zoom = slideEl.querySelector(ZOOM_IMAGE_SELECTOR);
    if (!zoom) return;

    // Remember the theme's own zoom URL once (data-src until first hover, src after)
    if (!zoom.dataset.kunstliOrigZoom) {
      zoom.dataset.kunstliOrigZoom = zoom.dataset.src || zoom.getAttribute('src');
    }
    // Stop the theme from lazy-loading the original over our image on hover
    zoom.removeAttribute('data-src');
    zoom.setAttribute('src', src);

    var link = slideEl.querySelector(ZOOM_LINK_SELECTOR);
    if (link) link.classList.remove('media--zoom-not-loaded');
  }

  function resetZoomImage(slideEl) {
    var zoom = slideEl.querySelector(ZOOM_IMAGE_SELECTOR);
    if (!zoom || !zoom.dataset.kunstliOrigZoom) return;
    zoom.setAttribute('src', zoom.dataset.kunstliOrigZoom);
    delete zoom.dataset.kunstliOrigZoom;
  }

  /* ---------- Slide states ---------- */

  function restoreSlide(slideEl) {
    if (!slideEl || slideEl.dataset.mediaType !== 'image') return;

    var originalSrc = getOriginalSrc(slideEl);
    var img = slideEl.querySelector(IMAGE_SELECTOR);
    var gallery = slideEl.closest(GALLERY_SELECTOR);

    withoutObservingImages(gallery, function () {
      if (img && originalSrc
          && (img.getAttribute('src') !== originalSrc || img.getAttribute('srcset'))) {
        img.setAttribute('src', originalSrc);
        img.removeAttribute('srcset'); // JSON only gives one URL per image
      }
      resetZoomImage(slideEl);
    });

    slideEl.removeAttribute('data-inkybay-preview');
  }

  function applyPreview(slideEl) {
    if (!slideEl || !previewSrc) return;
    var img = slideEl.querySelector(IMAGE_SELECTOR);
    var gallery = slideEl.closest(GALLERY_SELECTOR);

    withoutObservingImages(gallery, function () {
      if (img && (img.getAttribute('src') !== previewSrc || img.getAttribute('srcset'))) {
        img.setAttribute('src', previewSrc);
        img.removeAttribute('srcset'); // srcset would win over src in the browser
      }
      setZoomImage(slideEl, previewSrc);
    });

    slideEl.setAttribute('data-inkybay-preview', '1');
  }

  // Single source of truth: hero gets the preview (while active), everything
  // else gets its real photo.
  function enforce(gallery) {
    var hero = getHeroSlide(gallery);
    getSlides(gallery).forEach(function (slideEl) {
      if (slideEl === hero && previewActive && previewSrc) {
        applyPreview(slideEl);
      } else {
        restoreSlide(slideEl);
      }
    });
  }

  function scrollToHero(gallery) {
    var hero = getHeroSlide(gallery);
    if (!hero || gallery.dataset.layout === 'stacked') return;
    if (getCurrentSlide(gallery) === hero) return;
    var viewer = gallery.querySelector('.media-viewer');
    if (!viewer) return;
    // Our own scroll must not count as "shopper browsed away"
    autoScrolling = true;
    // Theme's own scroll handler updates counter, thumbs and arrows
    viewer.scrollTo({ left: hero.offsetLeft, behavior: 'smooth' });
  }

  function showPreview(gallery, src) {
    previewSrc = src;
    previewActive = true;
    enforce(gallery);
  }

  // Shopper browsed the gallery: hero goes back to its real photo
  function clearPreview() {
    var gallery = getGallery();
    previewActive = false;
    if (gallery) enforce(gallery);
  }

  /* ---------- InkyBay write detection ---------- */

  function firstSrcsetUrl(srcset) {
    return srcset ? srcset.trim().split(/\s+/)[0] : null;
  }

  function handleImageMutations(mutations) {
    var gallery = getGallery();
    if (!gallery) return;

    // Direct-file picks: InkyBay's slow data: PNG is not needed, enforce() reverts it
    if (hasClickedSwatch && !lastPickIsDirect && Date.now() <= acceptWritesUntil) {
      // Pick up whatever InkyBay just wrote, on whichever slide it wrote it
      mutations.forEach(function (mutation) {
        var img = mutation.target;
        if (!(img.matches && img.matches(IMAGE_SELECTOR))) return;
        var slideEl = img.closest(SLIDE_SELECTOR);
        if (!slideEl) return;

        var written = mutation.attributeName === 'srcset'
          ? firstSrcsetUrl(img.getAttribute('srcset'))
          : img.getAttribute('src');
        if (written && written !== getOriginalSrc(slideEl)) {
          previewSrc = written;
          previewActive = true;
          if (lastSwatchEl) previewBySwatch.set(lastSwatchEl, written);
        }
      });
    }

    enforce(gallery);
  }

  function startObservingImages(gallery) {
    imageObserver.observe(gallery, {
      attributes: true,
      attributeFilter: ['src', 'srcset'],
      subtree: true
    });
  }

  /* ---------- Listeners ---------- */

  // Any browsing by the shopper (thumb, arrow, swipe/scroll) ends the preview
  function initGalleryListeners(gallery) {
    var thumbs = gallery.querySelector('.media-thumbs');
    if (thumbs) {
      thumbs.addEventListener('click', function () { setTimeout(clearPreview, 0); });
    }

    var controls = gallery.querySelector('.media-ctrl');
    if (controls) {
      controls.addEventListener('click', function () { setTimeout(clearPreview, 350); });
    }

    var viewer = gallery.querySelector('.media-viewer');
    if (viewer) {
      var scrollTimer;
      viewer.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          if (autoScrolling) {   // our own scroll to the hero has settled
            autoScrolling = false;
            return;
          }
          clearPreview();
        }, 150);
      });
    }
  }

  function stripSelectedClass() {
    document.querySelectorAll('.' + SWATCH_SELECTED_CLASS).forEach(function (el) {
      el.classList.remove(SWATCH_SELECTED_CLASS);
    });
  }

  function initForcedDeselect() {
    stripSelectedClass();
    deselectObserver = new MutationObserver(function () {
      if (hasClickedSwatch) return;
      stripSelectedClass();
    });
    deselectObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
  }

  // True when an InkyBay option element carries an image ("SPACE::<file>|...")
  function hasOptionImage(el) {
    var part = el && el.getAttribute && el.getAttribute('pro_image_part');
    return !!(part && /::[^|\s]+\|/.test(part));
  }

  /* ---------- Direct option image files (skip InkyBay's canvas) ---------- */

  // CDN folder, from the store id in InkyBay's own markup
  function getOptionImageBase() {
    if (optionImageBase) return optionImageBase;
    var sources = [document.getElementById('efl_product_image_s')];
    var icon = document.querySelector(OPTION_SET_SELECTOR + ' label[style*="option_images"]');
    if (icon) sources.push({ textContent: icon.getAttribute('style') });

    for (var i = 0; i < sources.length; i++) {
      var text = sources[i] && sources[i].textContent;
      var m = text && text.match(/cdn\.inkybay\.com\/shopify\/resources\/(\d+)\//);
      if (m) {
        optionImageBase = 'https://cdn.inkybay.com/shopify/resources/' + m[1] + '/option_images/';
        return optionImageBase;
      }
    }
    return OPTION_IMAGE_BASE_FALLBACK;
  }

  // "SPACE::1791327982.png|~PRO_IMG_PART~|0,0,800,1000|..." -> full CDN URL
  function getDirectImageUrl(el) {
    var part = el && el.getAttribute && el.getAttribute('pro_image_part');
    var m = part && part.match(/::([^|\s]+)\|/);
    return m ? getOptionImageBase() + m[1] : null;
  }

  // Download every option image once, quietly, after the page has loaded.
  // InkyBay builds its option HTML late, so retry a few times.
  var preloadDone = false;
  function preloadOptionImages(attempt) {
    if (preloadDone) return;
    attempt = attempt || 0;
    var els = document.querySelectorAll(OPTION_SET_SELECTOR + ' [pro_image_part]');
    if (!els.length) {
      if (attempt < 8) setTimeout(function () { preloadOptionImages(attempt + 1); }, 1000);
      return;
    }
    preloadDone = true;
    var seen = {};
    Array.prototype.forEach.call(els, function (el) {
      var url = getDirectImageUrl(el);
      if (!url || seen[url]) return;
      seen[url] = true;
      var img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }

  // The element that identifies the picked value: radio input, selected
  // <option>, or the default colour swatch itself
  function getOptionKey(el) {
    if (!el) return null;
    if (el.tagName === 'SELECT') return el.options[el.selectedIndex] || null;
    if (el.tagName === 'INPUT') return el;
    var input = el.querySelector && el.querySelector('input[type="radio"]');
    return input || el;
  }

  function onOptionPicked(keyEl) {
    if (!keyEl) return;

    if (!hasClickedSwatch) {
      hasClickedSwatch = true;
      if (deselectObserver) {
        deselectObserver.disconnect();
        deselectObserver = null;
      }
    }

    lastSwatchEl = keyEl;
    acceptWritesUntil = Date.now() + ACCEPT_WINDOW_MS;

    var gallery = getGallery();
    if (!gallery) return;

    // Option with an image file: show InkyBay's original file right away
    var direct = getDirectImageUrl(keyEl);
    lastPickIsDirect = !!direct;
    if (direct) previewBySwatch.set(keyEl, direct);

    // Known option: show its preview now (also covers re-clicking an option
    // InkyBay won't re-fire for). Otherwise (default colour swatches)
    // InkyBay's write is picked up by the image observer.
    var known = previewBySwatch.get(keyEl);
    if (known) showPreview(gallery, known);

    // Bring the shopper to the hero, where the preview lives
    scrollToHero(gallery);
  }

  function initSwatchListener() {
    // Clicks on swatches / radios (also catches re-clicking the selected one)
    document.addEventListener('click', function (evt) {
      var swatchEl = evt.target.closest(SWATCH_SELECTOR);
      if (!swatchEl) return;
      var keyEl = getOptionKey(swatchEl);
      // Option-set swatches/radios without an image: nothing to preview
      if (keyEl !== swatchEl && !hasOptionImage(keyEl)) return;
      onOptionPicked(keyEl);
    }, true); // capture phase: flips before InkyBay's own handlers run

    // Changes on any option-set field: covers dropdowns and keyboard use
    document.addEventListener('change', function (evt) {
      var field = evt.target;
      if (!field.closest || !field.closest(OPTION_SET_SELECTOR)) return;
      var keyEl = getOptionKey(field);
      if (!hasOptionImage(keyEl)) return; // text, number, upload, no-image options
      onOptionPicked(keyEl);
    }, true); // capture phase: runs before InkyBay's inline onchange
  }

  function init() {
    var gallery = getGallery();
    if (!gallery) return;

    initGalleryListeners(gallery);
    initSwatchListener();
    initForcedDeselect();

    imageObserver = new MutationObserver(handleImageMutations);
    startObservingImages(gallery);

    // Whatever InkyBay did before the JSON arrived, wipe it now
    loadOriginalImages(gallery).then(function () {
      enforce(gallery);
    });

    // Warm the cache with option images once the page itself is done
    if (document.readyState === 'complete') {
      setTimeout(preloadOptionImages, 1000);
    } else {
      window.addEventListener('load', function () { setTimeout(preloadOptionImages, 1000); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();